import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plug, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import { z } from "zod";

const schema = z.object({
  api_url: z.string().trim().url({ message: "Enter a valid URL like https://yoursite.com/wp-json" }).max(500),
  username: z.string().trim().min(1, { message: "Username is required" }).max(100),
  app_password: z.string().trim().min(1, { message: "Application Password is required" }).max(200),
});

type Props = {
  siteId: string;
  domain: string | null;
  technicalConfig: any;
};

export default function WordPressConnectionCard({ siteId, domain, technicalConfig }: Props) {
  const queryClient = useQueryClient();
  const existing = technicalConfig?.wordpress || {};

  const defaultApiUrl = domain
    ? `https://${domain.replace(/^https?:\/\//, "").replace(/\/+$/, "")}/wp-json`
    : "";

  const [apiUrl, setApiUrl] = useState<string>(existing.api_url || defaultApiUrl);
  const [username, setUsername] = useState<string>(existing.username || "");
  const [appPassword, setAppPassword] = useState<string>(existing.app_password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof z.infer<typeof schema>, string>>>({});

  useEffect(() => {
    setApiUrl(existing.api_url || defaultApiUrl);
    setUsername(existing.username || "");
    setAppPassword(existing.app_password || "");
  }, [existing.api_url, existing.username, existing.app_password, defaultApiUrl]);

  const isConnected = !!(existing.api_url && existing.username && existing.app_password);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const parsed = schema.safeParse({ api_url: apiUrl, username, app_password: appPassword });
      if (!parsed.success) {
        const fieldErrors: typeof errors = {};
        for (const issue of parsed.error.issues) {
          fieldErrors[issue.path[0] as keyof typeof errors] = issue.message;
        }
        setErrors(fieldErrors);
        throw new Error("Please fix the highlighted fields.");
      }
      setErrors({});

      const newTech = {
        ...(technicalConfig || {}),
        wordpress: {
          api_url: parsed.data.api_url.replace(/\/+$/, ""),
          username: parsed.data.username,
          app_password: parsed.data.app_password,
          connected_at: new Date().toISOString(),
        },
      };

      const { error } = await supabase
        .from("websites")
        .update({ technical_config: newTech })
        .eq("id", siteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Site connection saved.");
    },
    onError: (e: any) => {
      toast.error(e.message || "Could not save connection.");
    },
  });

  const handleTest = async () => {
    const parsed = schema.safeParse({ api_url: apiUrl, username, app_password: appPassword });
    if (!parsed.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as keyof typeof errors] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error("Please fill all fields correctly before testing.");
      return;
    }
    setErrors({});
    setTesting(true);
    setTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("test-wp-connection", {
        body: {
          api_url: parsed.data.api_url.replace(/\/+$/, ""),
          username: parsed.data.username,
          app_password: parsed.data.app_password,
        },
      });
      if (error) {
        setTestResult({ ok: false, message: error.message || "Could not run the connection test." });
      } else if (data?.ok) {
        setTestResult({ ok: true, message: data.message || "Connection successful" });
        toast.success("Connection successful");
      } else {
        setTestResult({ ok: false, message: data?.message || "Connection failed." });
      }
    } catch (e: any) {
      setTestResult({ ok: false, message: e?.message || "Could not reach the test service." });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Plug className="h-4 w-4 text-foreground" />
            <h4 className="text-sm font-medium text-foreground">Site Connection</h4>
            {isConnected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 px-2 py-0.5 text-[10px] font-medium">
                <CheckCircle2 className="h-3 w-3" /> Connected
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Required to push articles to this site. Use an{" "}
            <span className="text-foreground font-medium">Application Password</span>
            {" "}— not the user's main password.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground block mb-1">REST API URL</label>
          <input
            type="url"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="https://yoursite.com/wp-json"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {errors.api_url ? (
            <p className="text-[10px] text-destructive mt-1">{errors.api_url}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-1">Usually your site URL with <code className="text-foreground">/wp-json</code> appended.</p>
          )}
        </div>

        <div>
          <label className="text-[11px] font-medium text-muted-foreground block mb-1">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="editor"
            autoComplete="off"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {errors.username && <p className="text-[10px] text-destructive mt-1">{errors.username}</p>}
        </div>

        <div>
          <label className="text-[11px] font-medium text-muted-foreground block mb-1">Application Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
              autoComplete="new-password"
              className="w-full rounded-md border border-border bg-background pl-3 pr-9 py-2 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
          {errors.app_password ? (
            <p className="text-[10px] text-destructive mt-1">{errors.app_password}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-1">
              Generate one under Users → Profile → Application Passwords.
            </p>
          )}
        </div>
      </div>

      {testResult && (
        <div
          className={`rounded-md border p-2.5 flex items-start gap-2 ${
            testResult.ok
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-destructive/30 bg-destructive/5"
          }`}
        >
          {testResult.ok ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
          )}
          <p className="text-[11px] text-foreground">{testResult.message}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleTest}
          disabled={testing || saveMutation.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
        >
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
          Test Connection
        </button>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || testing}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {isConnected ? "Update Connection" : "Save Connection"}
        </button>
      </div>
    </div>
  );
}
